/** @format */

import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Scene7Service, TaggingService } from '@shared/data-access/services';

import { DomSanitizer } from '@angular/platform-browser';
import { LazyLoadDirective } from '@shared/utils/directives';

@Component({
  selector: 'shared-media-component-video',
  styleUrl: './media-video.component.scss',
  templateUrl: './media-video.component.html',
  providers: [TaggingService],
  imports: [LazyLoadDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaVideoComponent {
  // Inputs as signals
  readonly altText = input<string>();
  readonly image = input<string>();
  readonly mobileImg = input<string>();
  readonly gif = input<string>();
  readonly gifMobile = input<string>();
  readonly itemTitle = input<string>();
  readonly animation = input<string>();
  readonly youtubeId = input<string>();
  readonly willLazyLoad = input<boolean>(true);
  readonly useScene7URLs = input<boolean>(true);
  readonly showControls = input<boolean>(false);
  readonly autoplay = input<boolean>(true);
  // Restricts native <video> controls (e.g. "nofullscreen nodownload"). Defaults
  // to the historical behavior of disabling both fullscreen and download.
  // Consumers that need native fullscreen (e.g. the in-house image gallery)
  // can override this, e.g. to 'nodownload' only.
  readonly controlsList = input<string>('nofullscreen nodownload');

  // Services
  private readonly taggingService = inject(TaggingService);
  private readonly scene7Service = inject(Scene7Service);
  private readonly sanitizer = inject(DomSanitizer);

  // Computed signals for template values
  protected readonly urlSafe = computed(() => {
    const id = this.youtubeId();
    return id
      ? this.sanitizer.bypassSecurityTrustResourceUrl(`https://www.youtube.com/embed/${id}`)
      : null;
  });

  protected readonly mediaTitleText = computed(() => {
    const alt = this.altText();
    const title = this.itemTitle();
    return alt || title ? this.taggingService.altTag(alt || '', title || '') : null;
  });

  protected readonly posterUrl = computed(() => {
    const img = this.image();
    if (this.useScene7URLs()) {
      return img ? this.scene7Service.scene7Url(img, undefined, undefined) : null;
    } else {
      return img || null;
    }
  });

  protected readonly animationUrl = computed(() => {
    const anim = this.animation();
    if (this.useScene7URLs()) {
      return anim ? this.scene7Service.scene7Url(anim, undefined, true) : null;
    } else {
      return anim || null;
    }
  });

  startAutoplay(videoElement: HTMLVideoElement) {
    if (this.autoplay()) {
      videoElement.play().catch(() => {
        // Handle autoplay errors, prevents unnecessary console errors in browsers that block autoplay
      });
    }
  }

  loadedMetadata(videoElement: HTMLVideoElement) {
    if (this.autoplay()) {
      videoElement.muted = true;
    }
  }
}
